import { useNavigate } from "react-router-dom";
import SEO from "../components/seo/SEO";
import { Section, Fade, Heading, Label, Text, Button, C } from "../components/ui";

export default function Services() {
  const navigate = useNavigate();

  const goToContact = () => {
    navigate("/contact");
  };

  const goToPricing = () => {
    navigate("/pricing");
  };

  const pillars = [
    {
      n: "01",
      icon: "◇",
      title: "Strategy & Growth",
      role: "The foundation",
      desc: "Market analysis, customer journey mapping, and strategic planning that drives real revenue — no guesswork.",
      inc: [
        "Brand Strategy",
        "Go-To-Market Strategy",
        "Funnel & Conversion Planning",
        "Customer Journey Mapping",
        "Market & Competitor Research",
        "Analytics & Growth Planning",
      ],
    },
    {
      n: "02",
      icon: "○",
      title: "Branding & Creative",
      role: "Visual identity",
      desc: "Logos, visual systems, and messaging that make local businesses look premium and memorable.",
      inc: [
        "Brand Identity (logo, visual system)",
        "Brand Guidelines",
        "Copywriting & Messaging",
        "Ad Creatives",
        "Social Media Design",
        "Visual Direction",
      ],
    },
    {
      n: "03",
      icon: "□",
      title: "Web & Development",
      role: "Digital presence",
      desc: "Websites, landing pages, and e-commerce stores built to convert visitors into customers.",
      inc: [
        "Website Design & Development",
        "Landing Pages",
        "E-commerce Development",
        "UI/UX Design",
        "Website Optimization (speed, CRO)",
      ],
    },
    {
      n: "04",
      icon: "△",
      title: "Content & Social",
      role: "Audience engagement",
      desc: "Short-form video, social media management, and content that stops the scroll.",
      inc: [
        "Short-form Video (Reels, TikTok, Shorts)",
        "Long-form Content (YouTube, blogs)",
        "Content Strategy & Planning",
        "Social Media Management",
        "Influencer / UGC Content",
      ],
    },
    {
      n: "05",
      icon: "◇",
      title: "Performance & Automation",
      role: "Revenue engine",
      desc: "Paid ads, SEO, email, CRM, and automation working together to scale your business.",
      inc: [
        "Paid Ads (Meta, Google, TikTok)",
        "SEO & Organic Growth",
        "Email Marketing",
        "CRM & Automation",
        "Retargeting & Funnels",
        "Conversion Optimization",
      ],
    },
  ];

  const industries = [
    {
      icon: "🍽",
      n: "Restaurants & Cafés",
      d: "Food content that makes people hungry before they walk in.",
    },
    {
      icon: "✂️",
      n: "Salons & Spas",
      d: "Transformation content that builds your brand and fills your chair.",
    },
    {
      icon: "💈",
      n: "Barbershops",
      d: "Culture, community, and craft — captured on video.",
    },
    {
      icon: "🛍",
      n: "Retail & Boutiques",
      d: "Product showcases and local brand storytelling.",
    },
    {
      icon: "🔧",
      n: "Service Businesses",
      d: "Professional content that converts leads into customers.",
    },
    {
      icon: "📍",
      n: "Local Brands",
      d: "Whatever you do — we make you impossible to ignore.",
    },
  ];

  return (
    <>
      <SEO
        title="What We Do"
        description="Full-service digital marketing for local businesses. Strategy, branding, web development, content, social media, and automation that drives real results."
      />

      {/* Hero Section */}
      <Section background={C.navy}>
        <Fade>
          <div style={{ textAlign: "center", maxWidth: "800px", margin: "0 auto" }}>
            <Label light>What We Do</Label>
            <Heading light style={{ marginBottom: "24px" }}>
              Everything Your Business Needs to Grow
            </Heading>
            <Text light size="lg" style={{ marginBottom: "40px" }}>
              We're not just another marketing agency. We're your complete growth partner —
              from strategy to execution, we handle it all.
            </Text>
            <Button variant="coral" onClick={goToContact}>
              Get Your Free Strategy Call
            </Button>
          </div>
        </Fade>
      </Section>

      {/* Services Pillars */}
      <Section background={C.offWhite}>
        <Fade>
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <Label>Our Services</Label>
            <Heading size={2}>Five Pillars of Growth</Heading>
          </div>
        </Fade>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "32px",
          }}
        >
          {pillars.map((pillar, index) => (
            <Fade key={index} delay={index * 0.1}>
              <div
                style={{
                  background: C.white,
                  padding: "32px",
                  borderRadius: "12px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
                  height: "100%",
                }}
              >
                <div style={{ marginBottom: "16px" }}>
                  <Text
                    size="sm"
                    style={{
                      color: C.coral,
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      fontFamily: C.mono,
                    }}
                  >
                    {pillar.n}
                  </Text>
                  <div style={{ fontSize: "2rem", marginTop: "8px" }}>{pillar.icon}</div>
                </div>

                <Heading
                  size={3}
                  style={{ marginBottom: "8px", fontSize: "1.25rem" }}
                >
                  {pillar.title}
                </Heading>

                <Text
                  size="sm"
                  style={{
                    color: C.coral,
                    fontWeight: 600,
                    marginBottom: "12px",
                    textTransform: "uppercase",
                    fontSize: "11px",
                    letterSpacing: "0.05em",
                  }}
                >
                  {pillar.role}
                </Text>

                <Text
                  size="sm"
                  style={{
                    color: C.gray600,
                    marginBottom: "20px",
                    lineHeight: 1.6,
                  }}
                >
                  {pillar.desc}
                </Text>

                <div style={{ borderTop: `1px solid ${C.gray100}`, paddingTop: "16px" }}>
                  <Text
                    size="sm"
                    style={{
                      color: C.gray500,
                      fontWeight: 600,
                      marginBottom: "12px",
                      fontSize: "12px",
                    }}
                  >
                    INCLUDES:
                  </Text>
                  <ul style={{ margin: 0, paddingLeft: "16px", listStyle: "none" }}>
                    {pillar.inc.map((item, idx) => (
                      <li
                        key={idx}
                        style={{
                          marginBottom: "6px",
                          fontSize: "13px",
                          color: C.gray600,
                          position: "relative",
                          paddingLeft: "16px",
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            left: 0,
                            color: C.coral,
                          }}
                        >
                          •
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Fade>
          ))}
        </div>
      </Section>

      {/* Industries */}
      <Section background={C.navy}>
        <Fade>
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <Label light>Industries We Serve</Label>
            <Heading size={2} light>
              Local Businesses We Know Inside & Out
            </Heading>
          </div>
        </Fade>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "32px",
          }}
        >
          {industries.map((industry, index) => (
            <Fade key={index} delay={index * 0.1}>
              <div
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  padding: "28px 24px",
                  borderRadius: "12px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "3rem", marginBottom: "16px" }}>
                  {industry.icon}
                </div>
                <Heading
                  size={3}
                  light
                  style={{ marginBottom: "12px", fontSize: "1.125rem" }}
                >
                  {industry.n}
                </Heading>
                <Text light size="sm" style={{ color: C.gray300, lineHeight: 1.6 }}>
                  {industry.d}
                </Text>
              </div>
            </Fade>
          ))}
        </div>
      </Section>

      {/* Process */}
      <Section background={C.white}>
        <Fade>
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <Label>Our Process</Label>
            <Heading size={2}>How We Work</Heading>
          </div>
        </Fade>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "32px",
            maxWidth: "1000px",
            margin: "0 auto",
          }}
        >
          {[
            {
              n: "01",
              t: "We Analyze",
              d: "Free audit of your business, local market, and competition. We find exactly where the growth opportunity is.",
            },
            {
              n: "02",
              t: "We Strategize",
              d: "A custom growth plan using the pillars that matter most for your business. No templates — real strategy.",
            },
            {
              n: "03",
              t: "We Execute",
              d: "Content, ads, SEO, automation — our team handles all of it while you focus on running your business.",
            },
            {
              n: "04",
              t: "We Scale",
              d: "Monthly reporting, strategy calls, and continuous optimization. Results compound every month.",
            },
          ].map((step, index) => (
            <Fade key={index} delay={index * 0.1}>
              <div>
                <Text
                  size="sm"
                  style={{
                    color: C.coral,
                    fontWeight: 600,
                    marginBottom: "12px",
                    fontFamily: C.mono,
                    letterSpacing: "0.1em",
                  }}
                >
                  {step.n}
                </Text>
                <Heading size={3} style={{ marginBottom: "12px" }}>
                  {step.t}
                </Heading>
                <Text size="sm" style={{ color: C.gray600, lineHeight: 1.6 }}>
                  {step.d}
                </Text>
              </div>
            </Fade>
          ))}
        </div>
      </Section>

      {/* CTA Section */}
      <Section background={C.navyLight}>
        <Fade>
          <div style={{ textAlign: "center", maxWidth: "700px", margin: "0 auto" }}>
            <Heading light style={{ marginBottom: "24px" }}>
              Ready to Grow Your Business?
            </Heading>
            <Text light size="lg" style={{ marginBottom: "40px" }}>
              Let's discuss which services will drive the most growth for your business.
              Free consultation, no obligation.
            </Text>
            <div style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap" }}>
              <Button variant="coral" size="lg" onClick={goToContact}>
                Get Free Consultation
              </Button>
              <Button variant="outline" size="lg" onClick={goToPricing}>
                View Pricing
              </Button>
            </div>
          </div>
        </Fade>
      </Section>
    </>
  );
}
